import { Icon, Edit, IconSize, Dialog } from '@soteria/design-system-ve';
import { useState } from 'react';

import { RedRingOfDeathIcon } from './RedRingOfDeathIcon';

interface ErrorDisplayProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string | string[];
  onRetry?: () => void;
  onBack?: () => void;
  onSignIn?: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  correlationId?: string;
}

export function ErrorDisplay({ isOpen, onClose, title, message, onRetry, onBack, onSignIn, size = 'md', correlationId }: ErrorDisplayProps) {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyCorrelationId = async () => {
    if (correlationId) {
      try {
        await navigator.clipboard.writeText(correlationId);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy correlation ID:', err);
      }
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }

    onClose();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }

    onClose();
  };

  const handleSignIn = () => {
    if (onSignIn) {
      onSignIn();
    }

    onClose();
  };

  // Build actions based on provided handlers
  const primaryAction = onSignIn
    ? { label: 'Sign In', onClick: handleSignIn, variant: 'primary' as const }
    : onRetry
      ? { label: 'Try Again', onClick: handleRetry, variant: 'primary' as const }
      : undefined;

  const secondaryAction = onBack ? { label: 'Go Back', onClick: handleBack, variant: 'secondary' as const } : undefined;

  return (
    <Dialog
      cancelAction={{ label: 'Close' }}
      isOpen={isOpen}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      size={size}
      title={title}
      onClose={onClose}
    >
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <RedRingOfDeathIcon size={128} />
      </div>

      {typeof message === 'string' ? (
        <p
          style={{
            textAlign: 'center',
            marginBottom: '1.5rem',
            lineHeight: '1.5',
            color: 'var(--color-text-secondary)',
          }}
        >
          {message}
        </p>
      ) : (
        <ul
          style={{
            textAlign: 'left',
            marginBottom: '1.5rem',
            lineHeight: '1.5',
            color: 'var(--color-text-secondary)',
            paddingLeft: '2rem',
          }}
        >
          {message.map(msg => (
            <li key={`error-${msg}`}>
              <p>{msg}</p>
            </li>
          ))}
        </ul>
      )}

      {correlationId && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            color: 'var(--color-text-tertiary)',
            fontFamily: 'monospace',
          }}
        >
          <span>Correlation ID: {correlationId}</span>
          <Icon
            icon={Edit}
            iconSize={IconSize.XSmall}
            style={{
              cursor: 'pointer',
              color: copySuccess ? 'var(--color-success)' : 'var(--color-text-secondary)',
            }}
            title={copySuccess ? 'Copied!' : 'Copy correlation ID'}
            onClick={handleCopyCorrelationId}
          />
        </div>
      )}
    </Dialog>
  );
}
